import {
  LayoutDashboard,
  Newspaper,
  Coins,
  CalendarDays,
  Sprout,
  Target,
  Receipt,
  ChevronsLeft,
  ChevronRight,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { NavLink, useCurrentPath, isPathActive } from "@/lib/nav";
import { IconButton } from "@/components/IconButton";
import { Menu } from "@/components/Menu";

interface NavLeaf {
  label: string;
  to: string;
  disabled?: boolean;
}

interface NavItem {
  label: string;
  icon: LucideIcon;
  to?: string;
  disabled?: boolean;
  children?: NavLeaf[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

// Supplier-portal information architecture. Tailored for nature-project
// partners: see contribution activity allocated to their projects/goals and
// download payout statements. Every item maps to a real, existing route.
const sections: NavSection[] = [
  {
    label: "Workspace",
    items: [
      { label: "Home", icon: LayoutDashboard, to: "/" },
      { label: "Feed", icon: Newspaper,       to: "/feed" },
    ],
  },
  {
    label: "Contributions",
    items: [
      { label: "Contributions", icon: Coins,        to: "/contributions" },
      { label: "Calendar",      icon: CalendarDays, to: "/calendar" },
    ],
  },
  {
    label: "Projects & impact",
    items: [
      { label: "Projects", icon: Sprout, to: "/projects" },
      { label: "Goals",    icon: Target, to: "/goals" },
    ],
  },
  {
    label: "Payouts",
    items: [
      { label: "Statements", icon: Receipt, to: "/account/statements" },
    ],
  },
];

interface PortalSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobile?: boolean;
  onClose?: () => void;
}

export function PortalSidebar({
  collapsed,
  onToggle,
  mobile,
  onClose,
}: PortalSidebarProps) {
  const showCollapsed = mobile ? false : collapsed;

  return (
    <aside
      className={cn(
        "flex h-full flex-col border-r border-border-subtle bg-subtle",
        "transition-[width] duration-[var(--duration-default)] ease-[var(--ease-out)]",
        mobile ? "w-[260px]" : showCollapsed ? "w-[60px]" : "w-[244px]",
      )}
    >
      <div className="flex h-14 items-center justify-between gap-2 px-3">
        <div className="flex min-w-0 items-center gap-2">
          <img
            src="/brand-mark.svg"
            alt=""
            aria-hidden="true"
            className="size-7 shrink-0"
          />
          {!showCollapsed && (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate font-brand text-base font-medium tracking-tight text-fg">
                stabiliti
              </span>
              <span className="truncate text-[11px] text-fg-muted">
                Supplier portal
              </span>
            </div>
          )}
        </div>
        {mobile ? (
          <IconButton aria-label="Close menu" size="sm" onClick={onClose}>
            <X />
          </IconButton>
        ) : (
          !showCollapsed && (
            <IconButton aria-label="Collapse sidebar" size="sm" onClick={onToggle}>
              <ChevronsLeft />
            </IconButton>
          )
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        {sections.map((section) => (
          <div key={section.label} className="mb-4">
            {!showCollapsed && (
              <div className="px-2 pb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-fg-subtle">
                {section.label}
              </div>
            )}
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <li key={item.label}>
                  {item.children ? (
                    <ParentNavItem item={item} collapsed={showCollapsed} />
                  ) : (
                    <LeafNavItem
                      item={item as NavLeaf & { icon: LucideIcon }}
                      collapsed={showCollapsed}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {showCollapsed && (
        <div className="border-t border-border-subtle p-2">
          <IconButton
            aria-label="Expand sidebar"
            size="sm"
            onClick={onToggle}
            className="w-full"
          >
            <ChevronsLeft className="rotate-180" />
          </IconButton>
        </div>
      )}
    </aside>
  );
}

function LeafNavItem({
  item,
  collapsed,
}: {
  item: NavLeaf & { icon: LucideIcon };
  collapsed: boolean;
}) {
  const Icon = item.icon;
  if (item.disabled) {
    return (
      <span
        title={collapsed ? `${item.label} (coming soon)` : "Coming soon"}
        className={cn(
          "flex h-8 items-center gap-2 rounded-[var(--radius-md)] px-2 text-sm",
          "text-fg-disabled cursor-not-allowed",
          collapsed && "justify-center",
        )}
      >
        <Icon className="size-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </span>
    );
  }
  return (
    <NavLink
      to={item.to}
      end
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "flex h-8 items-center gap-2 rounded-[var(--radius-md)] px-2 text-sm",
          "transition-[background-color,color] duration-[var(--duration-micro)]",
          "text-fg-muted hover:bg-surface-hover hover:text-fg",
          isActive && "bg-accent-subtle text-accent-text font-medium hover:bg-accent-subtle",
          collapsed && "justify-center",
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

function ParentNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  const pathname = useCurrentPath();
  const childActive = !!item.children?.some((c) => !c.disabled && isPathActive(pathname, c.to));

  const [open, setOpen] = useState<boolean>(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  if (collapsed) {
    return (
      <Menu.Root>
        <Menu.Trigger asChild>
          <button
            type="button"
            title={item.label}
            className={cn(
              "flex h-8 w-full items-center justify-center rounded-[var(--radius-md)] px-2 text-sm",
              "text-fg-muted hover:bg-surface-hover hover:text-fg",
              childActive && "bg-accent-subtle text-accent-text",
            )}
          >
            <Icon className="size-4 shrink-0" />
          </button>
        </Menu.Trigger>
        <Menu.Content side="right" align="start" sideOffset={8}>
          <Menu.Label>{item.label}</Menu.Label>
          {item.children?.map((c) =>
            c.disabled ? (
              <Menu.Item key={c.to} disabled>{c.label}</Menu.Item>
            ) : (
              <FlyoutChildItem key={c.to} to={c.to} label={c.label} />
            ),
          )}
        </Menu.Content>
      </Menu.Root>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "flex h-8 w-full items-center gap-2 rounded-[var(--radius-md)] px-2 text-sm",
          "transition-[background-color,color] duration-[var(--duration-micro)]",
          "text-fg-muted hover:bg-surface-hover hover:text-fg",
          childActive && !open && "text-fg",
        )}
      >
        <Icon className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">{item.label}</span>
        <ChevronRight
          className={cn(
            "size-3.5 text-fg-subtle transition-transform duration-[var(--duration-default)] ease-[var(--ease-out)]",
            open && "rotate-90",
          )}
        />
      </button>
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-[var(--duration-default)] ease-[var(--ease-out)]",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
        aria-hidden={!open}
      >
        <ul
          className={cn(
            "min-h-0 overflow-hidden",
            "mt-0.5 ml-4 flex flex-col gap-0.5 border-l border-border-subtle pl-2",
          )}
        >
          {item.children?.map((c) => (
            <li key={c.to}>
              {c.disabled ? (
                <span
                  title="Coming soon"
                  className="flex h-7 items-center rounded-[var(--radius-sm)] px-2 text-[13px] text-fg-disabled cursor-not-allowed"
                >
                  {c.label}
                </span>
              ) : (
                <NavLink
                  to={c.to}
                  end
                  className={({ isActive }) =>
                    cn(
                      "flex h-7 items-center rounded-[var(--radius-sm)] px-2 text-[13px]",
                      "transition-[background-color,color] duration-[var(--duration-micro)]",
                      "text-fg-muted hover:bg-surface-hover hover:text-fg",
                      isActive && "text-accent-text font-medium",
                    )
                  }
                >
                  {c.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FlyoutChildItem({ to, label }: { to: string; label: string }) {
  const pathname = useCurrentPath();
  const active = pathname === to;
  return (
    <Menu.Item asChild>
      <NavLink
        to={to}
        className={cn(
          "flex w-full",
          active && "text-accent-text font-medium",
        )}
      >
        {label}
      </NavLink>
    </Menu.Item>
  );
}
